export const convertBase64 = (
  file: File,
): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = error => {
      reject(error);
    };
  });

export const createBlobUrlFromBase64 = (base64: string) => {
  const base64Data = base64.split(';base64,').pop() || '';

  const byteCharacters = Buffer.from(base64Data, 'base64');
  const byteArray = new Uint8Array(byteCharacters);

  const blob = new Blob([byteArray], { type: 'image/png' });

  const blobUrl = URL.createObjectURL(blob);

  return blobUrl;
};

const maxSize = 6 * 1024 * 1024; // 6MB

const maxWidthOrHeight = 1660;

interface CompressImage {
  file: File;
  quality?: number;
}

export const compressImage = async (params: CompressImage): Promise<File> => {
  const { file, quality = 1 } = params;

  if (file.size <= maxSize) return file;

  const img = new Image();
  const reader = new FileReader();

  await new Promise<void>((resolve, reject) => {
    reader.onload = () => {
      img.onload = () => {
        resolve();
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  const canvas = document.createElement('canvas');

  let { width } = img;
  let { height } = img;

  if (width > height) {
    if (width > maxWidthOrHeight) {
      height *= maxWidthOrHeight / width;
      width = maxWidthOrHeight;
    }
  } else if (height > maxWidthOrHeight) {
    width *= maxWidthOrHeight / height;
    height = maxWidthOrHeight;
  }

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context of canvas');

  ctx.drawImage(img, 0, 0, width, height);

  const imgBlob = await new Promise<Blob>(resolve => {
    canvas.toBlob(
      blob => {
        if (!blob) throw new Error('Could not create blob from canvas');

        resolve(blob);
      },
      file.type,
      quality,
    );
  });

  const compressedFile = new File([imgBlob], file.name, {
    type: file.type,
  });

  canvas.remove();

  if (compressedFile.size > maxSize) {
    return compressImage({ file: compressedFile, quality: quality - 0.1 });
  }

  return compressedFile;
};

export const getImageFileDimensions = (
  file: File,
): Promise<ImageDimensions> => {
  return new Promise(resolve => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        resolve({ height: img.height, width: img.width });
      };

      img.src = URL.createObjectURL(file);
    };

    reader.readAsDataURL(file);
  });
};

interface CheckImageSizeParams {
  min: ImageDimensions;
  image: ImageDimensions;
}

export const checkImageSize = (params: CheckImageSizeParams) => {
  const { min, image } = params;

  if (image.height < min.height || image.width < min.width) return false;

  const heightRatio = image.height / min.height;
  const widthRatio = image.width / min.width;

  if (heightRatio < 1 || widthRatio < 1) return false;

  return true;
};
